/**
 * Criação de cercas para paradas novas na alocação de passageiros em linhas.
 *
 * Espelha o pipeline do Módulo 1 da roteirização (Roteirizacao.vue): projeta o ponto do
 * passageiro na polyline da rota da linha, clusteriza apenas as paradas novas, cria as
 * cercas, reordena a sequência ao longo da rota e regrava `cercas_linhas`.
 *
 * Passageiro PCD é a exceção: a cerca nasce no endereço de casa; a rota é redesenhada
 * via /verso/solve (reordenação) com fallback /verso/plan. Sem PCD o ponto cai sobre a
 * polyline e o desenho não muda.
 */

import apiController from "@/utils/apiController";
import {
  closestPointOnPolyline,
  distanceAlongPolyline,
  extractLineStringCoordinates,
  haversineMeters,
} from "@/utils/polylineGeometry";
import { decodePlanGeometry } from "@/utils/routeTraceVroom";

/** Raio das cercas criadas para paradas de alocação (metros). */
export const NEW_STOP_FENCE_RADIUS_METERS = 75;

/** Valores de `id_tipo_cerca` usados em cercas_linhas. */
export const FENCE_TYPE = {
  POI: 1,
  START: 2,
  END: 3,
  GARAGE: 4,
  STOP: 5,
};

/** `tipos_rotas`: PRI=1, DES=2. */
export const TIPO_ROTA_DESLOCAMENTO = 2;

/** `id_sentido = -1`: a cerca/rota atende ida e volta ao mesmo tempo. */
export const SENTIDO_AMBOS = -1;

/** `sentidos`: ida = 1, 3, 4; volta = 2, 5, 6 (mesmo mapa usado no espelho da volta). */
const SENTIDOS_VOLTA = new Set([2, 5, 6]);

/**
 * True quando o sentido é de volta. Os pontos inicial (tipo 2) e final (tipo 3) são
 * cadastrados pela geometria da ida e valem para os dois sentidos, então na volta os
 * papéis se invertem: a rota parte do ponto final e termina no ponto inicial.
 */
export function isSentidoVolta(sentido) {
  return SENTIDOS_VOLTA.has(Number(sentido));
}

/**
 * True se a cerca pertence ao sentido de trabalho. Cercas de ida e volta (-1) — o caso
 * comum dos pontos inicial e final — pertencem a qualquer sentido.
 */
export function fenceBelongsToSentido(cerca, sentido) {
  const s = Number(cerca && cerca.id_sentido);
  if (!Number.isFinite(s)) return false;
  const alvo = Number(sentido);
  if (!Number.isFinite(alvo)) return true;
  return s === alvo || s === SENTIDO_AMBOS || alvo === SENTIDO_AMBOS;
}

export function isTerminusFenceType(tipo) {
  const t = Number(tipo);
  return t === FENCE_TYPE.START || t === FENCE_TYPE.END;
}

function sentidosOverlapForOrder(a, b) {
  if (a == null || b == null || Number.isNaN(Number(a)) || Number.isNaN(Number(b))) {
    return true;
  }
  const na = Number(a);
  const nb = Number(b);
  return na === nb || na === -1 || nb === -1;
}

function isOperationalFenceForOrder(cerca) {
  if (!cerca) return false;
  const tipo = Number(cerca.id_tipo_cerca);
  return tipo !== FENCE_TYPE.GARAGE && tipo !== FENCE_TYPE.POI;
}

export function lineHasDisplacement(rotas, sentido) {
  const sentidoNum = Number(sentido);
  return (rotas || []).some((r) => {
    if (!r) return false;
    const tipo = Number(r.id_tipo_rota);
    const cod = String(r.cod_tipo_rota || "").toUpperCase();
    const isDes = tipo === TIPO_ROTA_DESLOCAMENTO || cod === "DES";
    if (!isDes) return false;
    if (!Number.isFinite(sentidoNum)) return true;
    const rs = Number(r.id_sentido);
    return rs === sentidoNum || rs === -1;
  });
}

function fenceHasCoords(c) {
  const lat = Number(c && c.lat_centro);
  const lng = Number(c && c.lng_centro);
  return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
}

function operationalFencesWithCoords(cercas) {
  return (cercas || []).filter((c) => {
    if (!c) return false;
    const t = Number(c.id_tipo_cerca);
    if (t === FENCE_TYPE.GARAGE || t === FENCE_TYPE.POI) return false;
    return fenceHasCoords(c);
  });
}

function resolvePinStart(proposed, options = {}) {
  if (Object.prototype.hasOwnProperty.call(options, "pinStart")) return !!options.pinStart;
  return lineHasDisplacement(options.rotas, proposed && proposed.id_sentido);
}

/** Tipo 3 sempre; tipo 2 só com deslocamento no sentido. */
export function isPinnedTerminusFence(cerca, options = {}) {
  if (!cerca) return false;
  const tipo = Number(cerca.id_tipo_cerca);
  if (tipo === FENCE_TYPE.END) return true;
  if (tipo !== FENCE_TYPE.START) return false;
  return resolvePinStart(cerca, options);
}

/**
 * True if applying `proposed` as pinned terminus would leave it off first/last.
 * Em qualquer sentido: tipo 2 (ponto inicial) primeiro, tipo 3 (ponto final) último.
 */
export function wouldBreakTerminusOrder(existingCercas, proposed, exclude = null, options = {}) {
  if (!proposed || !isPinnedTerminusFence(proposed, options)) return false;
  const tipo = Number(proposed.id_tipo_cerca);
  const ordem = Number(proposed.ordem_parada);
  if (!Number.isFinite(ordem)) return true;
  const others = (existingCercas || []).filter((c) => {
    if (!isOperationalFenceForOrder(c)) return false;
    if (!sentidosOverlapForOrder(c.id_sentido, proposed.id_sentido)) return false;
    if (
      exclude &&
      Number(c.id_cerca) === Number(exclude.id_cerca) &&
      Number(c.id_sentido) === Number(exclude.id_sentido)
    ) {
      return false;
    }
    return true;
  });
  const otherOrders = others
    .map((c) => Number(c.ordem_parada))
    .filter((n) => Number.isFinite(n));
  if (!otherOrders.length) return false;
  if (tipo === FENCE_TYPE.START) return ordem >= Math.min(...otherOrders);
  return ordem <= Math.max(...otherOrders);
}

/** True if a pinned terminus in `cercas` is not first/last in its direction. */
export function hasTerminusOrderViolation(cercas, options = {}) {
  const list = cercas || [];
  return list.some((c) => {
    if (!c) return false;
    return wouldBreakTerminusOrder(
      list,
      c,
      { id_cerca: c.id_cerca, id_sentido: c.id_sentido },
      options
    );
  });
}

/**
 * Insere uma cerca intermediária (tipo STOP) na lista, preservando terminais pinados
 * na 1ª/última ordem do sentido. Usado ao mover cerca entre linhas — a ordem final
 * ainda pode ser otimizada pelo /solve depois.
 *
 * @returns {{ list: object[], inserted: object }}
 */
export function insertFencePreservingTerminusOrder(existingCercas, fence, options = {}) {
  const list = (existingCercas || []).map((c) => ({ ...c }));
  const sentido = fence && fence.id_sentido;
  const same = list.filter(
    (c) =>
      isOperationalFenceForOrder(c) && sentidosOverlapForOrder(c.id_sentido, sentido)
  );
  const volta = isSentidoVolta(sentido);
  const lastPinned = same.find((c) => {
    if (!isPinnedTerminusFence(c, options)) return false;
    const tipo = Number(c.id_tipo_cerca);
    return (tipo === FENCE_TYPE.END && !volta) || (tipo === FENCE_TYPE.START && volta);
  });

  let insertOrdem;
  if (lastPinned && Number.isFinite(Number(lastPinned.ordem_parada))) {
    insertOrdem = Number(lastPinned.ordem_parada);
    for (const c of list) {
      if (!isOperationalFenceForOrder(c)) continue;
      if (!sentidosOverlapForOrder(c.id_sentido, sentido)) continue;
      const o = Number(c.ordem_parada) || 0;
      if (o >= insertOrdem) c.ordem_parada = o + 1;
    }
  } else {
    const orders = same
      .map((c) => Number(c.ordem_parada))
      .filter((n) => Number.isFinite(n));
    insertOrdem = orders.length ? Math.max(...orders) + 1 : 1;
  }

  const inserted = {
    ...fence,
    id_tipo_cerca: FENCE_TYPE.STOP,
    ordem_parada: insertOrdem,
  };
  list.push(inserted);
  return { list, inserted };
}

/** Ida: a origem é o ponto inicial (tipo 2) e o destino, o ponto final (tipo 3). */
function resolveTerminaisIda(ops) {
  const endFence =
    [...ops].reverse().find((c) => Number(c.id_tipo_cerca) === FENCE_TYPE.END) || null;
  const startFence = ops.find((c) => Number(c.id_tipo_cerca) === FENCE_TYPE.START) || null;
  return { startFence, endFence };
}

/**
 * Volta: a origem é o ponto inicial (tipo 2) cadastrado no próprio sentido de volta;
 * quando a volta não tem terminal próprio, é o ponto final (tipo 3) compartilhado
 * (`id_sentido = -1`), de onde a volta de fato parte.
 * O destino fica em aberto (`endFence: null`): a volta raramente tem uma cerca marcada
 * como terminal de chegada, e escolhê-la em linha reta erra onde a malha viária não
 * acompanha a geografia. Sem `vehicle.end` o solve devolve uma rota aberta e a última
 * parada dela — a mais distante da origem por distância real — encerra a rota, sem
 * custar nenhuma requisição a mais.
 */
function resolveTerminaisVolta(ops, sentido) {
  const startFence =
    ops.find(
      (c) =>
        Number(c.id_tipo_cerca) === FENCE_TYPE.START &&
        sentido != null &&
        Number(c.id_sentido) === Number(sentido)
    ) ||
    ops.find(
      (c) =>
        Number(c.id_tipo_cerca) === FENCE_TYPE.END && Number(c.id_sentido) === SENTIDO_AMBOS
    ) ||
    null;
  return { startFence, endFence: null };
}

/**
 * Monta jobs/vehicle do /verso/solve.
 * Na ida o ponto final (tipo 3) é o `vehicle.end` e nunca é job.
 * O ponto inicial (tipo 2) vira `vehicle.start` quando há deslocamento no sentido ou
 * quando `forcePinStart` obriga o ponto inicial a ancorar a roteirização.
 * `pinStart` explícito (true/false) vence as duas regras acima: com `false` o ponto
 * inicial vira job comum e pode ser reordenado como qualquer outra cerca. Na volta ele
 * é ignorado — a origem é de onde o veículo parte e fica sempre fixa.
 * Com `inverterTerminais` (sentido de volta), os terminais são resolvidos por
 * `resolveTerminaisVolta`: a origem vem do cadastro e o destino, da geografia.
 */
export function buildSolveJobsAndVehicle({
  cercas,
  hasDisplacement,
  serviceSec = 0,
  forcePinStart = false,
  pinStart: pinStartOverride = null,
  inverterTerminais = false,
  sentido = null,
}) {
  const ops = operationalFencesWithCoords(cercas);
  const { startFence, endFence } = inverterTerminais
    ? resolveTerminaisVolta(ops, sentido)
    : resolveTerminaisIda(ops);
  if (inverterTerminais) {
    if (!startFence) {
      return { error: true, message: "solve sem ponto de partida" };
    }
  } else if (!endFence) {
    return { error: true, message: "solve sem ponto final" };
  }
  const canPinStart = !!(
    startFence && (!endFence || String(startFence.id_cerca) !== String(endFence.id_cerca))
  );
  const intermediateFences = (pinned) =>
    ops.filter((c) => {
      if (endFence && String(c.id_cerca) === String(endFence.id_cerca)) return false;
      if (pinned && String(c.id_cerca) === String(startFence.id_cerca)) return false;
      return true;
    });

  // Na volta a origem é imposta: o veículo sai de onde ele está, não há como soltá-la.
  let pinStart = inverterTerminais
    ? canPinStart
    : pinStartOverride != null
      ? !!(pinStartOverride && canPinStart)
      : !!((hasDisplacement || forcePinStart) && canPinStart);
  let jobsList = intermediateFences(pinStart);
  if (!jobsList.length && pinStart) {
    // Linha só com ponto inicial e final: o inicial volta a ser job para o solve ter parada.
    pinStart = false;
    jobsList = intermediateFences(false);
  }
  if (!jobsList.length) {
    return { error: true, message: "solve sem paradas intermediárias" };
  }

  const vehicle = {
    id: 1,
    profile: "car",
    capacity: [9999],
  };
  // Sem `end` a rota é aberta e quem decide onde ela termina é o solve.
  if (endFence) {
    vehicle.end = [Number(endFence.lng_centro), Number(endFence.lat_centro)];
  }
  if (pinStart) {
    vehicle.start = [Number(startFence.lng_centro), Number(startFence.lat_centro)];
  }

  vehicle.capacity = [Math.max(9999, (jobsList.length + 2) * 10)];
  const jobById = new Map();
  const jobs = jobsList.map((c, i) => {
    const id = i + 1;
    jobById.set(id, c);
    return {
      id,
      location: [Number(c.lng_centro), Number(c.lat_centro)],
      service: Math.max(0, Number(serviceSec) || 0),
    };
  });

  return {
    jobs,
    vehicle,
    jobById,
    jobsList,
    endFence,
    startFence,
    pinStart,
    /** True quando o fim da rota sai do resultado do solve, não do cadastro. */
    endFromSolve: !endFence,
  };
}

function withPlanSteps(built, orderedCercas) {
  if (!built || built.error) return built;
  const idOrder = [];
  const seen = new Set();
  (orderedCercas || []).forEach((c) => {
    if (!c || c.id_cerca == null) return;
    const id = String(c.id_cerca);
    if (seen.has(id)) return;
    seen.add(id);
    idOrder.push(id);
  });
  const byId = new Map((built.jobsList || []).map((c) => [String(c.id_cerca), c]));
  const jobsList = [];
  idOrder.forEach((id) => {
    if (byId.has(id)) jobsList.push(byId.get(id));
  });
  (built.jobsList || []).forEach((c) => {
    if (c && c.id_cerca != null && !seen.has(String(c.id_cerca))) {
      jobsList.push(c);
    }
  });
  const jobById = new Map();
  const jobs = jobsList.map((c, i) => {
    const id = i + 1;
    jobById.set(id, c);
    return {
      id,
      location: [Number(c.lng_centro), Number(c.lat_centro)],
      service: Number((built.jobs && built.jobs[0] && built.jobs[0].service) || 0),
    };
  });
  const vehicle = { ...built.vehicle };
  const steps = [];
  if (Array.isArray(vehicle.start)) steps.push({ type: "start" });
  jobs.forEach((j) => steps.push({ type: "job", id: j.id }));
  if (Array.isArray(vehicle.end)) steps.push({ type: "end" });
  vehicle.steps = steps;
  vehicle.capacity = [Math.max(9999, (jobs.length + 2) * 10)];
  return { ...built, jobs, vehicle, jobById, jobsList };
}

/**
 * Garante tipo 3 no ponto final; promove/rebaixa tipo 2 conforme pinStart.
 * A ordem dos terminais não depende do sentido: tipo 2 na primeira parada, tipo 3 na última.
 */
export function retagOperationalTypes(
  ordered,
  sentido,
  { pinStart, startFence, endFence, preservarTerminais = false } = {}
) {
  const list = (ordered || []).map((c) => ({ ...c }));

  if (preservarTerminais) {
    // Volta: os terminais são compartilhados com a ida (id_sentido = -1). Retipá-los aqui
    // quebraria a ida, então só as paradas intermediárias são normalizadas.
    list.forEach((c) => {
      const t = Number(c.id_tipo_cerca);
      if (
        t === FENCE_TYPE.GARAGE ||
        t === FENCE_TYPE.POI ||
        t === FENCE_TYPE.START ||
        t === FENCE_TYPE.END
      ) {
        return;
      }
      c.id_tipo_cerca = FENCE_TYPE.STOP;
    });
    return list;
  }

  const endId = endFence && endFence.id_cerca != null ? String(endFence.id_cerca) : null;
  const startId = startFence && startFence.id_cerca != null ? String(startFence.id_cerca) : null;

  list.forEach((c) => {
    if (endId && String(c.id_cerca) === endId) c.id_tipo_cerca = FENCE_TYPE.END;
  });

  if (pinStart && startId) {
    list.forEach((c) => {
      const t = Number(c.id_tipo_cerca);
      if (t === FENCE_TYPE.GARAGE || t === FENCE_TYPE.POI) return;
      if (String(c.id_cerca) === startId) c.id_tipo_cerca = FENCE_TYPE.START;
      else if (String(c.id_cerca) !== endId && t === FENCE_TYPE.START) {
        c.id_tipo_cerca = FENCE_TYPE.STOP;
      }
    });
    return list;
  }

  const operationalIdx = [];
  list.forEach((c, i) => {
    const t = Number(c.id_tipo_cerca);
    if (t === FENCE_TYPE.GARAGE || t === FENCE_TYPE.POI) return;
    operationalIdx.push(i);
  });
  const opsWithoutEnd = operationalIdx.filter((i) => String(list[i].id_cerca) !== endId);
  const promoteIdx = opsWithoutEnd[0];

  list.forEach((c, i) => {
    const t = Number(c.id_tipo_cerca);
    if (t === FENCE_TYPE.GARAGE || t === FENCE_TYPE.POI) return;
    if (endId && String(c.id_cerca) === endId) {
      c.id_tipo_cerca = FENCE_TYPE.END;
      return;
    }
    c.id_tipo_cerca = i === promoteIdx ? FENCE_TYPE.START : FENCE_TYPE.STOP;
  });
  return list;
}

export function collectFenceTypeChanges(before, after) {
  const beforeById = new Map();
  (before || []).forEach((c) => {
    if (c && c.id_cerca != null) beforeById.set(String(c.id_cerca), Number(c.id_tipo_cerca));
  });
  const changes = [];
  (after || []).forEach((c) => {
    if (!c || c.id_cerca == null) return;
    const prev = beforeById.get(String(c.id_cerca));
    const next = Number(c.id_tipo_cerca);
    if (prev != null && Number.isFinite(prev) && Number.isFinite(next) && prev !== next) {
      changes.push({ id_cerca: c.id_cerca, tipo_antes: prev, tipo_depois: next });
    }
  });
  return changes;
}

export function fenceSnapshotForHistory(cercas) {
  return (cercas || []).map((c) => ({
    id_cerca: c && c.id_cerca,
    nome_cerca: c && c.nome_cerca,
    id_tipo_cerca: c && c.id_tipo_cerca,
    ordem_parada: c && c.ordem_parada,
    id_sentido: c && c.id_sentido,
    lat_centro: c && c.lat_centro,
    lng_centro: c && c.lng_centro,
  }));
}

/** Código `valido` da validação de endereço que exige criação de ponto. */
export const VALIDO_CRIACAO_PONTO = 3;

const REVERSE_GEOCODE_CHUNK = 12;

/**
 * Enriquece cercas com lat/lng numéricos a partir de lat_centro/lng_centro
 * (ou latitude/longitude / lat/lng).
 */
export function enrichCercasWithCoords(cercas) {
  return (cercas || []).map((c) => {
    if (!c || typeof c !== "object") return c;
    const lat =
      c.lat_centro != null
        ? Number(c.lat_centro)
        : c.latitude != null
        ? Number(c.latitude)
        : c.lat != null
        ? Number(c.lat)
        : NaN;
    const lng =
      c.lng_centro != null
        ? Number(c.lng_centro)
        : c.longitude != null
        ? Number(c.longitude)
        : c.lng != null
        ? Number(c.lng)
        : NaN;
    return {
      ...c,
      lat_centro: Number.isFinite(lat) ? lat : c.lat_centro,
      lng_centro: Number.isFinite(lng) ? lng : c.lng_centro,
    };
  });
}

/**
 * PCD / CRUD: /verso/solve (sem steps) reordena jobs.
 * Ponto final (tipo 3) é sempre o fim da rota, em qualquer sentido.
 * Ponto inicial (tipo 2) é pinado quando há perna de deslocamento ou `forcePinStart`.
 */
export async function solveReorderLineWithPcd({
  orderedCercas,
  serviceSec = 0,
  sentido,
  hasDisplacement = false,
  forcePinStart = false,
  pinStart = null,
  inverterTerminais = false,
} = {}) {
  const enriched = enrichCercasWithCoords(orderedCercas || []);
  const built = buildSolveJobsAndVehicle({
    cercas: enriched,
    hasDisplacement,
    serviceSec,
    forcePinStart,
    pinStart,
    inverterTerminais,
    sentido,
  });
  if (built.error) return built;

  const { jobs, vehicle, jobById, jobsList, endFence, startFence } = built;
  const pinStartResolvido = built.pinStart;

  try {
    const solveRes = await apiController.request(apiController.endpoints.versoSolve, {
      data: { jobs, vehicles: [vehicle] },
      silent: true,
      config: { timeout: 120000 },
    });
    const data = solveRes.data;
    if (!data || data.code !== 0 || !Array.isArray(data.routes) || !data.routes.length) {
      const detail = (data && (data.message || data.error)) || "solve sem rotas";
      return { error: true, message: detail };
    }
    if (Array.isArray(data.unassigned) && data.unassigned.length) {
      return { error: true, message: "solve com jobs não atribuídos" };
    }

    const route = data.routes[0];
    const jobSteps = (route.steps || []).filter(
      (s) => String(s.type || "").toLowerCase() === "job"
    );
    if (jobSteps.length !== jobsList.length) {
      return { error: true, message: "solve perdeu paradas intermediárias" };
    }

    const orderedJobs = [];
    for (const step of jobSteps) {
      const cerca = jobById.get(Number(step.id));
      if (!cerca) {
        return { error: true, message: "solve retornou job desconhecido" };
      }
      orderedJobs.push(cerca);
    }

    const sequence = [
      ...(pinStartResolvido && startFence ? [startFence] : []),
      ...orderedJobs,
      ...(endFence ? [endFence] : []),
    ];
    // Rota aberta (volta): a última parada escolhida pelo solve é o fim da rota.
    const terminalFence = endFence || sequence[sequence.length - 1] || null;

    const usedIds = new Set(
      sequence.filter((c) => c && c.id_cerca != null).map((c) => String(c.id_cerca))
    );
    const leftovers = enriched.filter((c) => {
      if (!c || c.id_cerca == null) return false;
      if (Number(c.id_tipo_cerca) === FENCE_TYPE.GARAGE) return true;
      if (Number(c.id_tipo_cerca) === FENCE_TYPE.POI) return true;
      return !usedIds.has(String(c.id_cerca));
    });

    const newOrdered = retagOperationalTypes([...sequence, ...leftovers], sentido, {
      pinStart: pinStartResolvido,
      startFence,
      endFence: terminalFence,
      preservarTerminais: inverterTerminais,
    });
    const tiposAlterados = collectFenceTypeChanges(enriched, newOrdered);

    const etasByCerca = {};
    const durationSec = Number(route.duration) || 0;
    if (pinStartResolvido && startFence && startFence.id_cerca != null) {
      etasByCerca[String(startFence.id_cerca)] = 0;
    }
    jobSteps.forEach((step) => {
      const cerca = jobById.get(Number(step.id));
      if (cerca && cerca.id_cerca != null) {
        etasByCerca[String(cerca.id_cerca)] = Number(step.arrival) || 0;
      }
    });
    if (terminalFence && terminalFence.id_cerca != null) {
      etasByCerca[String(terminalFence.id_cerca)] = durationSec;
    }

    if (!route.geometry) {
      return { error: true, message: "solve sem geometria" };
    }
    const decoded = decodePlanGeometry(route.geometry);
    if (!decoded) {
      return { error: true, message: "solve sem geometria decodificável" };
    }

    return {
      orderedCercas: newOrdered,
      etasByCerca,
      geojson: decoded.geojson,
      shape: decoded.shape,
      distance: Number(route.distance) || 0,
      durationSec,
      tiposAlterados,
      hasDisplacement: !!hasDisplacement,
      pinStart: pinStartResolvido,
      planStopCoords: newOrdered
        .filter((c) => Number(c.id_tipo_cerca) !== FENCE_TYPE.GARAGE)
        .map((c) => [Number(c.lng_centro), Number(c.lat_centro)])
        .filter((c) => Number.isFinite(c[0]) && Number.isFinite(c[1])),
      planStartCoord: vehicle.start || null,
      planEndCoord: vehicle.end || null,
      replaceRoute: true,
    };
  } catch (e) {
    const detail =
      (e &&
        e.response &&
        e.response.data &&
        (e.response.data.message || e.response.data.error)) ||
      (e && e.message) ||
      "verso/solve falhou";
    return { error: true, message: detail };
  }
}

export function parseRotaGeojson(raw) {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (e) {
    return null;
  }
}

/**
 * Extrai os pontos [{ lat, lng }] do traçado de uma rota, aceitando GeoJSON
 * (LineString/MultiLineString/Feature/FeatureCollection) ou o campo `shape`
 * ([{ lt, lg }] em JSON ou já como array).
 */
export function rotaShapePontos(rota) {
  if (!rota) return [];

  const gj = parseRotaGeojson(rota.geojson);
  const geom = gj && gj.type === "Feature" ? gj.geometry : gj;
  if (geom && geom.type === "FeatureCollection") {
    const first = (geom.features || []).find((f) => f && f.geometry);
    return first ? rotaShapePontos({ geojson: first.geometry }) : [];
  }
  if (geom && Array.isArray(geom.coordinates)) {
    let coords = [];
    if (geom.type === "LineString") coords = geom.coordinates;
    else if (geom.type === "MultiLineString") coords = geom.coordinates.flat();
    const pontos = coords
      .map((c) => {
        if (!Array.isArray(c)) return null;
        const lng = Number(c[0]);
        const lat = Number(c[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng };
      })
      .filter(Boolean);
    if (pontos.length >= 2) return pontos;
  }

  let shape = rota.shape;
  if (typeof shape === "string") {
    try {
      shape = JSON.parse(shape);
    } catch (e) {
      shape = null;
    }
  }
  if (!Array.isArray(shape)) return [];
  return shape
    .map((p) => {
      if (!p || typeof p !== "object") return null;
      const lat = Number(p.lt != null ? p.lt : p.lat);
      const lng = Number(p.lg != null ? p.lg : p.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { lat, lng };
    })
    .filter(Boolean);
}

/** Distância haversine em metros entre dois pontos. */
function haversineMetros(a, b) {
  const R = 6371000;
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Distância de uma rota em metros. Prefere o valor cadastrado em `distancia`;
 * quando ele não está preenchido, mede o traçado da própria rota.
 */
export function rotaDistanciaMetros(rota) {
  if (!rota) return null;
  const stored = Number(rota.distancia);
  if (Number.isFinite(stored) && stored > 0) return stored;
  const pontos = rotaShapePontos(rota);
  if (pontos.length < 2) return null;
  let total = 0;
  for (let i = 1; i < pontos.length; i += 1) {
    total += haversineMetros(pontos[i - 1], pontos[i]);
  }
  return total > 0 ? total : null;
}

/**
 * Nome aceito pela validação do cadastro de cercas: letras, números, espaço, _ - . ,
 * com no máximo 200 caracteres.
 */
export function sanitizeFenceName(raw, fallback = "Parada alocada") {
  const cleaned = String(raw || "")
    .trim()
    .replace(/[^a-zA-Z0-9À-ÖØ-öø-ÿ\s_\-.,]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .substring(0, 200)
    .trim();
  return cleaned || fallback;
}

let configCache = null;

/**
 * Raios de clusterização e provedor de geocodificação (getallconfig), com cache de sessão.
 */
export async function fetchAllocationConfig() {
  if (configCache) return configCache;
  const fallback = {
    radiusConfig: { ideal: null, max: null },
    geocodeProvider: "google",
  };
  try {
    const res = await apiController.request(apiController.endpoints.getallConfig, {
      silent: true,
    });
    const data = res.data;
    const cfg = Array.isArray(data) ? data[0] : data;
    if (!cfg || typeof cfg !== "object" || cfg.erro) return fallback;
    const provider = String(cfg.api_geocodificacao_roteirizacao || "")
      .trim()
      .toLowerCase();
    configCache = {
      radiusConfig: {
        ideal: cfg.raio_ideal_validacao_candidatos,
        max: cfg.raio_maximo_validacao_candidatos,
      },
      geocodeProvider: provider === "here" ? "here" : "google",
    };
    return configCache;
  } catch (e) {
    return fallback;
  }
}

/**
 * Reverse geocode em lote pelo provedor configurado, preservando a ordem de entrada.
 *
 * Devolve o endereço formatado e também os campos estruturados que as duas rotas (Google e
 * HERE) já montam — quem só precisa do rótulo lê `formattedAddress` e ignora o resto.
 * @param {Array<{lat:number,lng:number}>} latlngs
 * @returns {Promise<Array<{ formattedAddress: string, error: boolean, logradouro?: string,
 *   numero?: string, bairro?: string, cidade?: string, estado?: string, cep?: string,
 *   pais?: string }>>}
 */
export async function reverseGeocodeBulk(latlngs, options = {}) {
  const { provider = "google", language = "pt-BR" } = options;
  const list = latlngs || [];
  const out = new Array(list.length).fill(null);
  if (!list.length) return out;
  const endpoint =
    provider === "here"
      ? apiController.endpoints.hereMapsGetReversegeocodebulk
      : apiController.endpoints.googleMapsGetReversegeocodebulk;

  for (let off = 0; off < list.length; off += REVERSE_GEOCODE_CHUNK) {
    const slice = list.slice(off, off + REVERSE_GEOCODE_CHUNK);
    try {
      const res = await apiController.request(endpoint, {
        data: { latlngs: slice, language },
        config: { timeout: 20000 },
        silent: true,
      });
      const data = res.data || {};
      let ord = data.orderedResults || null;
      if (!ord || ord.length !== slice.length) {
        ord = slice.map(
          (ll) =>
            (data.results || []).find(
              (r) =>
                Number(r.originalLat) === Number(ll.lat) &&
                Number(r.originalLng) === Number(ll.lng)
            ) || null
        );
      }
      for (let j = 0; j < slice.length; j++) {
        const row = ord[j];
        const ll = slice[j];
        out[off + j] =
          row && row.formattedAddress
            ? {
                formattedAddress: row.formattedAddress,
                error: false,
                logradouro: row.logradouro || null,
                numero: row.numero || null,
                bairro: row.bairro || null,
                cidade: row.cidade || null,
                estado: row.estado || null,
                cep: row.cep || null,
                pais: row.pais || null,
              }
            : { formattedAddress: `${ll.lat}, ${ll.lng}`, error: true };
      }
    } catch (e) {
      for (let j = 0; j < slice.length; j++) {
        const ll = slice[j];
        out[off + j] = { formattedAddress: `${ll.lat}, ${ll.lng}`, error: true };
      }
    }
  }
  return out;
}

/** Rotas da linha com geojson completo (getrotasporlinha). */
export async function fetchLineRoutes(idLinha) {
  try {
    const res = await apiController.request(apiController.endpoints.getRotasporlinha, {
      data: [idLinha],
      config: { timeout: 60000 },
      silent: true,
    });
    return Array.isArray(res.data)
      ? res.data.filter((r) => r && String(r.id_linha) === String(idLinha))
      : [];
  } catch (e) {
    return [];
  }
}

/** Cercas associadas à linha (getcercasporlinha). */
export async function fetchLineFences(idLinha) {
  try {
    const res = await apiController.request(apiController.endpoints.getCercasporlinha, {
      data: [idLinha],
      silent: true,
    });
    return Array.isArray(res.data)
      ? res.data.filter((r) => r && String(r.id_linha) === String(idLinha))
      : [];
  } catch (e) {
    return [];
  }
}

/** Rota principal da linha: tipo 2 quando existir, senão a primeira. */
export function selectMainRoute(rotaRows) {
  const rows = rotaRows || [];
  return rows.find((r) => Number(r.id_tipo_rota) === 2) || rows[0] || null;
}

/**
 * Rota principal filtrada pelo sentido informado. Sem rota exclusiva do sentido, usa a
 * rota marcada como ida e volta (`id_sentido = -1`), que atende os dois sentidos.
 */
export function selectMainRouteForSentido(rotaRows, sentido) {
  const sentidoNum = Number(sentido);
  const rows = rotaRows || [];
  const doSentido = rows.filter((r) => r && Number(r.id_sentido) === sentidoNum);
  if (doSentido.length) return selectMainRoute(doSentido);
  const ambos = rows.filter((r) => r && Number(r.id_sentido) === SENTIDO_AMBOS);
  return ambos.length ? selectMainRoute(ambos) : null;
}

/**
 * Chave estável da sequência operacional (sem garagem/POI) para comparar ordens.
 */
export function fenceOrderKey(cercas) {
  return (cercas || [])
    .filter((c) => {
      if (!c || c.id_cerca == null) return false;
      const t = Number(c.id_tipo_cerca);
      return t !== FENCE_TYPE.GARAGE && t !== FENCE_TYPE.POI;
    })
    .map((c) => String(c.id_cerca))
    .join("|");
}

/**
 * Sentido de trabalho, cercas daquele sentido em ordem de parada e as cercas que precisam
 * ser reinseridas depois do delete em massa (POIs e demais sentidos).
 * Cercas de ida e volta (`id_sentido = -1`) entram em `cercasBase` de qualquer sentido.
 * @param {Array} cercasLinha
 * @param {object|null} mainRota
 * @param {number|null} [sentidoOverride] sentido explícito (ex.: seletor do mapa)
 * @param {object} [options]
 * @param {boolean} [options.strict=false] Sem cerca alguma no sentido, devolve `cercasBase`
 *   vazio em vez de cair em todas as cercas da linha. Ida e volta têm paradas próprias:
 *   quando o sentido é escolhido pelo usuário, usar as do outro sentido roteirizaria errado.
 */
export function resolveDirectionContext(cercasLinha, mainRota, sentidoOverride, options = {}) {
  const rows = cercasLinha || [];
  const sentido =
    sentidoOverride != null && Number.isFinite(Number(sentidoOverride))
      ? Number(sentidoOverride)
      : Number(mainRota && mainRota.id_sentido) || Number(rows[0] && rows[0].id_sentido) || 1;
  const byOrder = (a, b) => (Number(a.ordem_parada) || 0) - (Number(b.ordem_parada) || 0);
  const cercasSentido = rows
    .filter((c) => {
      if (sentido === -1) return true;
      const s = Number(c.id_sentido);
      return s === sentido || s === -1;
    })
    .slice()
    .sort(byOrder);
  const strict = !!options.strict;
  return {
    sentido,
    cercasBase:
      cercasSentido.length || strict ? cercasSentido : rows.slice().sort(byOrder),
    preservedExtraCercas: rows.filter((c) => {
      if (Number(c.id_tipo_cerca) === FENCE_TYPE.POI) return true;
      if (sentido === -1) return false;
      const s = Number(c.id_sentido);
      return s !== sentido && s !== -1;
    }),
  };
}

/**
 * Mescla cercas existentes com as novas ordenando as intermediárias pela distância
 * percorrida ao longo da rota. Garagem (4) fica fora da sequência, origem (2) e
 * destino (3) permanecem nas extremidades.
 */
export function mergeFencesAlongRoute(existingCercas, newFences, coords, sentido) {
  const existing = existingCercas || [];
  const garage = existing.filter((c) => Number(c.id_tipo_cerca) === FENCE_TYPE.GARAGE);
  const origin = existing.filter((c) => Number(c.id_tipo_cerca) === FENCE_TYPE.START);
  const dest = existing.filter((c) => Number(c.id_tipo_cerca) === FENCE_TYPE.END);
  const intermediate = existing.filter((c) => {
    const t = Number(c.id_tipo_cerca);
    return (
      t !== FENCE_TYPE.POI &&
      t !== FENCE_TYPE.START &&
      t !== FENCE_TYPE.END &&
      t !== FENCE_TYPE.GARAGE
    );
  });

  const withAlong = (list, isNew) =>
    (list || []).map((c) => {
      const lat = Number(c.lat_centro != null ? c.lat_centro : c.latitude);
      const lng = Number(c.lng_centro != null ? c.lng_centro : c.longitude);
      const hit =
        Number.isFinite(lat) && Number.isFinite(lng)
          ? closestPointOnPolyline(lat, lng, coords)
          : null;
      return {
        id_cerca: c.id_cerca,
        lat_centro: lat,
        lng_centro: lng,
        endereco: c.endereco,
        nome_cerca: c.nome_cerca,
        id_tipo_cerca: isNew ? FENCE_TYPE.STOP : Number(c.id_tipo_cerca) || FENCE_TYPE.STOP,
        show_sinotico: c.show_sinotico != null ? c.show_sinotico : 1,
        tempo_chegada_minutos:
          c.tempo_chegada_minutos != null ? c.tempo_chegada_minutos : null,
        along: hit ? distanceAlongPolyline(coords, hit) : 0,
        isNew: !!isNew,
      };
    });

  const mids = [...withAlong(intermediate, false), ...withAlong(newFences, true)].sort(
    (a, b) => a.along - b.along
  );

  const originMapped = withAlong(origin, false).map((c) => ({
    ...c,
    id_tipo_cerca: FENCE_TYPE.START,
    along: -1,
  }));
  const destMapped = withAlong(dest, false).map((c) => ({
    ...c,
    id_tipo_cerca: FENCE_TYPE.END,
    along: Infinity,
  }));
  const garageMapped = withAlong(garage, false).map((c) => ({
    ...c,
    id_tipo_cerca: FENCE_TYPE.GARAGE,
  }));

  // Sem origem/destino tipados na linha, promove a primeira/última intermediária para
  // manter uma sequência operacional válida.
  let orderedMids = mids;
  if (!originMapped.length && orderedMids.length) {
    orderedMids = orderedMids.slice();
    orderedMids[0] = { ...orderedMids[0], id_tipo_cerca: FENCE_TYPE.START };
  }
  if (!destMapped.length && orderedMids.length > 1) {
    orderedMids = orderedMids.slice();
    orderedMids[orderedMids.length - 1] = {
      ...orderedMids[orderedMids.length - 1],
      id_tipo_cerca: FENCE_TYPE.END,
    };
  }

  const midForSeq = orderedMids.filter((c) => Number(c.id_tipo_cerca) === FENCE_TYPE.STOP);
  const origins = originMapped.length
    ? originMapped
    : orderedMids.filter((c) => Number(c.id_tipo_cerca) === FENCE_TYPE.START);
  const dests = destMapped.length
    ? destMapped
    : orderedMids.filter((c) => Number(c.id_tipo_cerca) === FENCE_TYPE.END);

  if (Number(sentido) === 1) {
    return [...garageMapped, ...origins, ...midForSeq, ...dests];
  }
  return [...dests, ...midForSeq, ...origins, ...garageMapped];
}

/**
 * Clusteriza apenas as paradas novas não-PCD entre si (as existentes da linha não entram).
 * PCD passa direto, pois cada um vira uma parada no próprio endereço.
 * @param {Function} pollProcess ({ idProcesso, waitMs, onDone, onError }) => void
 */
export function clusterNewStops(newStops, options = {}) {
  const { maxNumberofStops = null, radiusConfig = {}, pollProcess } = options;
  const stops = newStops || [];
  const pcdStops = stops.filter((s) => Number(s.pcd) === 1).map((s) => ({ ...s }));
  const regularStops = stops
    .filter((s) => Number(s.pcd) !== 1)
    .map((s) => ({
      ...s,
      latitude: s.latitude != null ? s.latitude : s.lat,
      longitude: s.longitude != null ? s.longitude : s.lng,
    }));

  if (!regularStops.length) return Promise.resolve(pcdStops);
  if (regularStops.length === 1 || typeof pollProcess !== "function") {
    return Promise.resolve([...pcdStops, ...regularStops]);
  }

  const payload = {
    stops: regularStops,
    maxNumberofStops:
      maxNumberofStops == null || maxNumberofStops === "" ? null : Number(maxNumberofStops),
    radiusConfig: { ideal: radiusConfig.ideal, max: radiusConfig.max },
  };

  return new Promise((resolve) => {
    apiController.request(apiController.endpoints.roteirizacaoClusterparadas, {
      data: payload,
      config: { timeout: 30000 },
      silent: true,
      onSuccess: (enqueueRes) => {
        const body = enqueueRes.data || {};
        if (body.error || !body.id_processo) {
          resolve({ error: true });
          return;
        }
        pollProcess({
          idProcesso: body.id_processo,
          waitMs: 120000,
          onDone: (resultado) => {
            const data = resultado || {};
            if (data.error) {
              resolve({ error: true });
              return;
            }
            if (data.noop || !Array.isArray(data.clusters)) {
              resolve([...pcdStops, ...regularStops]);
              return;
            }
            const out = [];
            for (const cluster of data.clusters) {
              const lat = Number(cluster.lat);
              const lng = Number(cluster.lng);
              if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
              out.push({
                latitude: lat,
                longitude: lng,
                lat,
                lng,
                endereco: cluster.endereco || null,
                passengerAmount: cluster.passengerAmount || 1,
                pcd: cluster.hasPcd ? 1 : 0,
              });
            }
            resolve([...pcdStops, ...out]);
          },
          onError: () => resolve({ error: true }),
        });
      },
      onError: () => resolve({ error: true }),
    });
  });
}

/** Fallback de `id_cerca` por `cod_cerca` quando o bulk não devolve insertIds. */
async function fetchFenceIdsByCod(cods) {
  try {
    const res = await apiController.request(apiController.endpoints.getCercasselect, {
      silent: true,
    });
    const rows = Array.isArray(res.data) ? res.data : [];
    const wanted = new Set(cods.map((c) => String(c)));
    const byCod = new Map();
    for (const row of rows) {
      if (!row || row.id_cerca == null) continue;
      const cod = String(row.cod_cerca);
      if (wanted.has(cod)) byCod.set(cod, row);
    }
    return byCod;
  } catch (e) {
    return new Map();
  }
}

/**
 * Cria uma cerca circular nova por parada (insertcercasbulk). Nunca reaproveita cerca
 * existente: o ponto de criação precisa ser um registro novo para entrar em cercas_linhas.
 * @returns {Promise<Array<object>|{ error: true }>} paradas com `id_cerca` resolvido
 */
export async function createFencesForStops(stops, options = {}) {
  const { reverseGeocode } = options;
  const list = (stops || []).filter((s) => {
    const lat = Number(s.latitude != null ? s.latitude : s.lat);
    const lng = Number(s.longitude != null ? s.longitude : s.lng);
    return Number.isFinite(lat) && Number.isFinite(lng);
  });
  if (!list.length) return [];

  const coords = list.map((s) => ({
    lat: Number(s.latitude != null ? s.latitude : s.lat),
    lng: Number(s.longitude != null ? s.longitude : s.lng),
  }));
  // Endereço da cerca é sempre o do centro dela (ponto na rota ou casa do PCD),
  // nunca o endereço cadastral de um passageiro atendido pelo cluster.
  const revs =
    typeof reverseGeocode === "function" ? await reverseGeocode(coords) : new Array(list.length);

  const stamp = Date.now().toString(36).toUpperCase();
  const bulkPayload = [];
  const meta = [];
  for (let i = 0; i < list.length; i++) {
    const { lat, lng } = coords[i];
    const rev = revs && revs[i];
    let endereco = "";
    if (rev && rev.formattedAddress && !rev.error) {
      endereco = String(rev.formattedAddress).trim().slice(0, 300);
    } else if (list[i].endereco) {
      endereco = String(list[i].endereco).trim().slice(0, 300);
    } else {
      endereco = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }
    // Código único para não cair no ON DUPLICATE KEY de cod_cerca — a aritmética de
    // insertIds do bulk depende de todas as linhas serem inserts de fato.
    const cod = `A${stamp}${i}`.substring(0, 20);
    const nomeBase = endereco ? endereco.split(",").slice(0, 2).join(", ").trim() : "";
    const nome = sanitizeFenceName(nomeBase, `Parada ${cod}`);
    bulkPayload.push({
      cod_cerca: cod,
      nome_cerca: nome,
      alarme_entrada: 0,
      alarme_saida: 0,
      endereco: endereco || null,
      shape: null,
      lat_centro: String(lat),
      lng_centro: String(lng),
      alarme_velocidade: 0,
      velocidade_maxima: null,
      geojson: null,
      is_circular: 1,
      raio_mts: NEW_STOP_FENCE_RADIUS_METERS,
      observacoes: null,
    });
    meta.push({ stop: { ...list[i], latitude: lat, longitude: lng, endereco }, cod, nome });
  }

  let insertData;
  try {
    const res = await apiController.request(apiController.endpoints.insertCercasbulk, {
      data: bulkPayload,
      silent: true,
    });
    insertData = res.data || {};
  } catch (e) {
    return { error: true };
  }
  if (insertData.error || insertData.erro) return { error: true };

  const insertIds = Array.isArray(insertData.insertIds) ? insertData.insertIds : [];
  const byCod = insertIds.length ? new Map() : await fetchFenceIdsByCod(meta.map((m) => m.cod));

  const result = [];
  for (let i = 0; i < meta.length; i++) {
    const m = meta[i];
    const fromIds = insertIds.find(
      (x) => x && (Number(x.index) === i || String(x.cod_cerca) === m.cod)
    );
    let idCerca = fromIds && fromIds.insertId != null ? Number(fromIds.insertId) : null;
    let nomeCerca = m.nome;
    if (idCerca == null) {
      const cerca = byCod.get(m.cod);
      if (cerca && cerca.id_cerca != null) {
        idCerca = Number(cerca.id_cerca);
        nomeCerca = cerca.nome_cerca || m.nome;
      }
    }
    if (idCerca == null) return { error: true };
    result.push({ ...m.stop, id_cerca: idCerca, nome_cerca: nomeCerca });
  }
  return result;
}

/**
 * Chama /verso/plan com a sequência de cercas.
 * Tipo 3 é sempre o fim da rota. Tipo 2 entra no vehicle com deslocamento ou `forcePinStart`.
 */
export async function planOrderedFencesRoute(orderedCercas, options = {}) {
  const serviceTime = Math.max(0, Number(options.serviceTime) || 0);
  const empty = { geojson: null, distance: 0, shape: null, etasByCerca: {}, durationSec: 0 };
  const built = withPlanSteps(
    buildSolveJobsAndVehicle({
      cercas: orderedCercas,
      hasDisplacement: !!options.hasDisplacement,
      serviceSec: serviceTime,
      forcePinStart: !!options.forcePinStart,
      pinStart: options.pinStart != null ? options.pinStart : null,
      inverterTerminais: !!options.inverterTerminais,
      sentido: options.sentido != null ? options.sentido : null,
    }),
    orderedCercas
  );
  if (built.error) {
    return { ...empty, error: true, message: built.message };
  }
  const { jobs, vehicle, jobById, jobsList, endFence, startFence, pinStart } = built;
  if (!jobsList.length) {
    return {
      ...empty,
      etasByCerca: endFence && endFence.id_cerca != null ? { [String(endFence.id_cerca)]: 0 } : {},
    };
  }

  try {
    const res = await apiController.request(apiController.endpoints.versoPlan, {
      data: { jobs, vehicles: [vehicle] },
      silent: true,
    });
    const planned = res.data;
    if (!planned || planned.code !== 0 || !Array.isArray(planned.routes) || !planned.routes.length) {
      return { error: true, message: (planned && (planned.message || planned.error)) || null };
    }
    const route = planned.routes[0];
    const decoded = decodePlanGeometry(route.geometry);
    if (!decoded) return { error: true, message: null };

    const durationSec = Number(route.duration) || 0;
    const etasByCerca = {};
    if (pinStart && startFence && startFence.id_cerca != null) {
      etasByCerca[String(startFence.id_cerca)] = 0;
    }
    if (endFence && endFence.id_cerca != null) {
      etasByCerca[String(endFence.id_cerca)] = durationSec;
    }
    (route.steps || [])
      .filter((s) => String(s.type || "").toLowerCase() === "job")
      .forEach((step) => {
        const stop = jobById.get(Number(step.id));
        if (stop && stop.id_cerca != null) {
          etasByCerca[String(stop.id_cerca)] = Number(step.arrival) || 0;
        }
      });

    return {
      geojson: decoded.geojson,
      distance: Number(route.distance) || 0,
      shape: decoded.shape,
      etasByCerca,
      durationSec,
      pinStart,
    };
  } catch (e) {
    const detail =
      (e && e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
      (e && e.message) ||
      null;
    return { error: true, message: detail };
  }
}

/** Índices das colunas de `insertCercaslinhabulk`. */
const BULK_TIPO = 2;
const BULK_SENTIDO = 5;
const BULK_ORDEM = 6;

/**
 * Ponto inicial/final marcado como ida e volta (-1) vale para os dois sentidos, então
 * precisa ficar antes/depois de TODAS as cercas operacionais da linha — inclusive as do
 * outro sentido, que são reinseridas com a ordem que já tinham. Sem isso a validação de
 * ordem dos terminais passa a acusar violação depois de reroteirizar um dos sentidos.
 *
 * @param {Array<Array>} fencesBulk linhas de `cercas_linhas` prontas para insert (mutado).
 */
function normalizeSharedTerminusOrder(fencesBulk) {
  const rows = fencesBulk || [];
  const isSharedTerminus = (row, tipo) =>
    Number(row[BULK_SENTIDO]) === SENTIDO_AMBOS && Number(row[BULK_TIPO]) === tipo;
  const inicio = rows.filter((r) => isSharedTerminus(r, FENCE_TYPE.START));
  const fim = rows.filter((r) => isSharedTerminus(r, FENCE_TYPE.END));
  if (!inicio.length && !fim.length) return;

  const demais = rows.filter((r) => {
    const tipo = Number(r[BULK_TIPO]);
    if (tipo === FENCE_TYPE.GARAGE || tipo === FENCE_TYPE.POI) return false;
    return !isSharedTerminus(r, FENCE_TYPE.START) && !isSharedTerminus(r, FENCE_TYPE.END);
  });
  const ordens = () => demais.map((r) => Number(r[BULK_ORDEM])).filter((n) => Number.isFinite(n));

  if (inicio.length) {
    const atuais = ordens();
    const min = atuais.length ? Math.min(...atuais) : 2;
    if (min <= 1) {
      const shift = 2 - min;
      demais.forEach((r) => {
        r[BULK_ORDEM] = Number(r[BULK_ORDEM]) + shift;
      });
    }
    inicio.forEach((r) => {
      r[BULK_ORDEM] = 1;
    });
  }
  if (fim.length) {
    const atuais = ordens();
    const max = atuais.length ? Math.max(...atuais) : 1;
    fim.forEach((r) => {
      r[BULK_ORDEM] = max + 1;
    });
  }
}

/**
 * Regrava `cercas_linhas` da linha com a nova sequência e, quando a rota foi redesenhada,
 * insere a rota nova e reassocia `rotas_linhas`.
 */
export async function persistLineFenceSequence(params) {
  const {
    idLinha,
    sentido,
    mainRota,
    otherRotas,
    orderedCercas,
    preservedExtraCercas,
    planGeojson,
    planDistance,
    planShape,
    etasByCerca,
    durationSec,
    replaceRoute = false,
  } = params;

  try {
    if (replaceRoute && planGeojson && mainRota) {
      const geojsonStr =
        typeof planGeojson === "string" ? planGeojson : JSON.stringify(planGeojson);
      // `rotas.rotas_uk01` é UNIQUE em `nome_rota` — não reutilizar o nome da rota atual.
      const suffix = Date.now().toString(36).toUpperCase().slice(-4);
      const codBase = String(mainRota.cod_rota || `L${idLinha}`).substring(0, 40);
      const nomeBase = String(
        mainRota.nome_rota || mainRota.cod_rota || `Rota ${idLinha}`
      ).substring(0, 45);
      const respInsert = await apiController.request(apiController.endpoints.insertRota, {
        data: {
          cod_rota: `${codBase}_${suffix}`.substring(0, 50),
          nome_rota: `${nomeBase}_${suffix}`.substring(0, 50),
          ativa: 1,
          shape: planShape || mainRota.shape || null,
          geojson: geojsonStr,
          distancia: planDistance != null ? planDistance : mainRota.distancia || 0,
          observacoes: mainRota.observacoes || null,
          // Clone automático: só empresa. Cliente opcional costuma vir inconsistente
          // no detalhe parcial da rota e dispara rotas.clienteEmpresaMismatch.
          id_empresa:
            mainRota.id_empresa != null && mainRota.id_empresa !== ""
              ? mainRota.id_empresa
              : null,
          id_cliente: null,
        },
        silent: true,
      });
      const insertBody = respInsert.data || {};
      if (insertBody.erro || insertBody.error) {
        return {
          ok: false,
          message:
            insertBody.mensagem ||
            insertBody.message ||
            insertBody.error ||
            "falha ao inserir rota",
        };
      }
      const novoIdRota = insertBody.id_rota != null ? insertBody.id_rota : insertBody.insertId;
      if (novoIdRota == null) {
        return { ok: false, message: "insert rota sem id_rota" };
      }

      await apiController.request(apiController.endpoints.deleteRotaslinhabulk, {
        data: { id_linha: idLinha },
        silent: true,
      });

      const sentidoMainRota =
        Number(mainRota.id_sentido) === SENTIDO_AMBOS ? SENTIDO_AMBOS : sentido;
      const rotasLinhaBulk = [
        [
          idLinha,
          novoIdRota,
          mainRota.id_tipo_rota != null ? mainRota.id_tipo_rota : 2,
          sentidoMainRota,
          1,
        ],
      ];
      for (const r of otherRotas || []) {
        if (r == null || r.id_rota == null) continue;
        rotasLinhaBulk.push([
          idLinha,
          r.id_rota,
          r.id_tipo_rota != null ? r.id_tipo_rota : 2,
          r.id_sentido != null ? r.id_sentido : sentido,
          1,
        ]);
      }
      await apiController.request(apiController.endpoints.insertRotaslinhabulk, {
        data: rotasLinhaBulk,
        silent: true,
      });
    }

    await apiController.request(apiController.endpoints.deleteCercasbylinha, {
      data: { id_linha: idLinha },
      silent: true,
    });

    const duracaoMin = Math.round((Number(durationSec) || 0) / 60);
    const fencesBulk = [];
    const fenceKeys = new Set();
    let ordem = 1;
    for (const c of orderedCercas || []) {
      if (c.id_cerca == null) continue;
      const tipo = Number(c.id_tipo_cerca) || FENCE_TYPE.STOP;
      // Cerca de ida e volta continua -1; as demais assumem o sentido de trabalho.
      const idSent = Number(c.id_sentido) === SENTIDO_AMBOS ? SENTIDO_AMBOS : sentido;
      const key = `${c.id_cerca}|${tipo}|${idSent}`;
      if (fenceKeys.has(key)) continue;
      fenceKeys.add(key);
      let tempoChegadaMinutos;
      if (tipo === FENCE_TYPE.START || tipo === FENCE_TYPE.GARAGE) {
        tempoChegadaMinutos = 0;
      } else if (tipo === FENCE_TYPE.END) {
        tempoChegadaMinutos =
          duracaoMin || (c.tempo_chegada_minutos != null ? c.tempo_chegada_minutos : null);
      } else {
        const etaSec = etasByCerca && etasByCerca[String(c.id_cerca)];
        tempoChegadaMinutos =
          etaSec != null && Number.isFinite(etaSec)
            ? Math.round(etaSec / 60)
            : c.tempo_chegada_minutos != null
            ? c.tempo_chegada_minutos
            : null;
      }
      fencesBulk.push([
        idLinha,
        c.id_cerca,
        tipo,
        tipo !== FENCE_TYPE.GARAGE ? 1 : 0,
        tempoChegadaMinutos,
        idSent,
        tipo === FENCE_TYPE.GARAGE ? 0 : ordem++,
      ]);
    }
    for (const c of preservedExtraCercas || []) {
      if (c.id_cerca == null) continue;
      const tipo = Number(c.id_tipo_cerca) || FENCE_TYPE.POI;
      const idSent = c.id_sentido != null ? c.id_sentido : sentido;
      const key = `${c.id_cerca}|${tipo}|${idSent}`;
      if (fenceKeys.has(key)) continue;
      fenceKeys.add(key);
      fencesBulk.push([
        idLinha,
        c.id_cerca,
        tipo,
        c.show_sinotico != null ? c.show_sinotico : 0,
        c.tempo_chegada_minutos != null ? c.tempo_chegada_minutos : null,
        idSent,
        c.ordem_parada != null ? c.ordem_parada : ordem++,
      ]);
    }
    normalizeSharedTerminusOrder(fencesBulk);

    if (fencesBulk.length) {
      await apiController.request(apiController.endpoints.insertCercaslinhabulk, {
        data: fencesBulk,
        silent: true,
      });
    }
    return { ok: true };
  } catch (e) {
    const data = e && e.response && e.response.data;
    const message =
      (data && (data.mensagem || data.message || data.msg || data.error)) ||
      (e && e.message) ||
      "falha ao persistir sequência de paradas";
    return { ok: false, message };
  }
}

/**
 * Pipeline completo de criação de cercas para paradas novas de uma linha.
 *
 * @param {object} params
 * @param {number} params.idLinha
 * @param {Array<object>} params.people passageiros com `id_pessoa`, `pcd`, `lat`/`lng` (casa)
 *   e, opcionalmente, `routeLat`/`routeLng` (ponto sugerido pela validação de endereço).
 * @param {Function} [params.pollProcess] polling do scheduler para a clusterização.
 * @param {number} [params.serviceTime] tempo de parada usado no /verso/plan (segundos).
 * @param {number|null} [params.maxNumberofStops] limite de paradas por cluster.
 * @param {string} [params.language] idioma do reverse geocode.
 * @returns {Promise<{ ok: boolean, reason?: string, warning?: string, routeReplaced?: boolean,
 *   fencesByPessoa?: object, createdFences?: Array<object> }>}
 */
export async function createFencesForAllocationStops(params) {
  const {
    idLinha,
    people,
    pollProcess,
    serviceTime = 0,
    maxNumberofStops = null,
    language = "pt-BR",
  } = params || {};
  if (idLinha == null || !(people || []).length) return { ok: false, reason: "noStops" };

  const config = await fetchAllocationConfig();
  const reverseGeocode = (coords) =>
    reverseGeocodeBulk(coords, { provider: config.geocodeProvider, language });

  const rotaRows = await fetchLineRoutes(idLinha);
  const mainRota = selectMainRoute(rotaRows);
  const coords = extractLineStringCoordinates(parseRotaGeojson(mainRota && mainRota.geojson));
  if (!coords || coords.length < 2) return { ok: false, reason: "noRoute" };

  const cercasLinha = await fetchLineFences(idLinha);
  if (!cercasLinha.length) return { ok: false, reason: "noFences" };
  const { sentido, cercasBase, preservedExtraCercas } = resolveDirectionContext(
    cercasLinha,
    mainRota
  );

  // Ponto da parada: não-PCD é projetado na polyline (mantém o desenho da rota);
  // PCD fica no endereço de casa, o que exige replanejar a rota adiante.
  const newStops = [];
  for (const person of people) {
    const isPcd = Number(person.pcd) === 1;
    let lat = Number(person.routeLat != null ? person.routeLat : person.lat);
    let lng = Number(person.routeLng != null ? person.routeLng : person.lng);
    if (isPcd) {
      const homeLat = Number(person.lat);
      const homeLng = Number(person.lng);
      if (Number.isFinite(homeLat) && Number.isFinite(homeLng)) {
        lat = homeLat;
        lng = homeLng;
      }
    } else if (Number.isFinite(lat) && Number.isFinite(lng)) {
      const hit = closestPointOnPolyline(lat, lng, coords);
      if (hit) {
        lat = hit.lat;
        lng = hit.lng;
      }
    }
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    newStops.push({
      id_pessoa: person.id_pessoa,
      latitude: lat,
      longitude: lng,
      lat,
      lng,
      endereco: isPcd ? String(person.endereco || "").trim() : "",
      passengerAmount: 1,
      pcd: isPcd ? 1 : 0,
    });
  }
  if (!newStops.length) return { ok: false, reason: "noCoords" };

  const clustered = await clusterNewStops(newStops, {
    maxNumberofStops,
    radiusConfig: config.radiusConfig,
    pollProcess,
  });
  if (!clustered || clustered.error) return { ok: false, reason: "clusterFailed" };

  const fenceStops = await createFencesForStops(clustered, { reverseGeocode });
  if (!fenceStops || fenceStops.error || !fenceStops.length) {
    return { ok: false, reason: "fenceCreateFailed" };
  }

  // Cada passageiro vai para a cerca nova mais próxima do seu ponto pré-cluster.
  const fencesByPessoa = {};
  for (const stop of newStops) {
    if (stop.id_pessoa == null) continue;
    let best = null;
    let bestDist = Infinity;
    for (const fs of fenceStops) {
      if (fs.id_cerca == null) continue;
      const d = haversineMeters(
        Number(stop.latitude),
        Number(stop.longitude),
        Number(fs.latitude),
        Number(fs.longitude)
      );
      if (Number.isFinite(d) && d < bestDist) {
        bestDist = d;
        best = fs;
      }
    }
    if (best) {
      fencesByPessoa[String(stop.id_pessoa)] = {
        id_cerca: best.id_cerca,
        nome_cerca: best.nome_cerca,
        latitude: Number(best.latitude),
        longitude: Number(best.longitude),
      };
    }
  }

  const newFenceObjs = fenceStops
    .filter((s) => s.id_cerca != null)
    .map((s) => ({
      id_cerca: s.id_cerca,
      lat_centro: Number(s.latitude),
      lng_centro: Number(s.longitude),
      endereco: s.endereco,
      nome_cerca: s.nome_cerca || s.endereco,
      id_tipo_cerca: FENCE_TYPE.STOP,
    }));
  let ordered = mergeFencesAlongRoute(cercasBase, newFenceObjs, coords, sentido);

  // Sem PCD a cerca caiu sobre a rota atual: geometria intacta, só a sequência muda.
  // Com PCD: /solve reordena intermediários; fallback /plan na ordem merge-along-route.
  let planResult = {
    geojson: null,
    distance: mainRota && mainRota.distancia != null ? Number(mainRota.distancia) : 0,
    shape: (mainRota && mainRota.shape) || null,
    etasByCerca: {},
    durationSec: 0,
    replaceRoute: false,
  };
  let warning = null;
  if (newStops.some((s) => Number(s.pcd) === 1)) {
    const serviceSec = Math.max(0, Number(serviceTime) || 0);
    const hasDisplacement = lineHasDisplacement(rotaRows, sentido);
    const solved = await solveReorderLineWithPcd({
      orderedCercas: ordered,
      serviceSec,
      sentido,
      hasDisplacement,
    });
    if (solved && !solved.error && solved.geojson) {
      if (Array.isArray(solved.orderedCercas) && solved.orderedCercas.length) {
        ordered = solved.orderedCercas;
      }
      planResult = { ...solved, replaceRoute: true };
    } else {
      if (solved && solved.message) warning = solved.message;
      const planned = await planOrderedFencesRoute(ordered, {
        serviceTime: serviceSec,
        sentido,
        hasDisplacement,
      });
      if (planned && !planned.error && planned.geojson) {
        const built = buildSolveJobsAndVehicle({
          cercas: ordered,
          hasDisplacement,
          serviceSec,
        });
        if (!built.error) {
          ordered = retagOperationalTypes(ordered, sentido, {
            pinStart: built.pinStart,
            startFence: built.startFence,
            endFence: built.endFence,
          });
        }
        planResult = { ...planned, replaceRoute: true };
        if (warning == null && planned.message) warning = planned.message;
      } else if (planned && planned.message) {
        warning = planned.message;
      }
      // PCD: mesmo se solve/plan falhar, segue gravando as cercas na geometria atual.
    }
  }

  const persisted = await persistLineFenceSequence({
    idLinha,
    sentido,
    mainRota,
    otherRotas: (rotaRows || []).filter(
      (r) => r && mainRota && String(r.id_rota) !== String(mainRota.id_rota)
    ),
    orderedCercas: ordered,
    preservedExtraCercas,
    planGeojson: planResult.geojson,
    planDistance: planResult.distance,
    planShape: planResult.shape,
    etasByCerca: planResult.etasByCerca,
    durationSec: planResult.durationSec,
    replaceRoute: !!planResult.replaceRoute,
  });
  if (!persisted || !persisted.ok) {
    return {
      ok: false,
      reason: "persistFailed",
      message: (persisted && persisted.message) || null,
    };
  }

  return {
    ok: true,
    fencesByPessoa,
    createdFences: newFenceObjs,
    orderedCercas: ordered,
    routeReplaced: !!planResult.replaceRoute,
    planResult,
    warning,
  };
}

/**
 * Atualiza geometria da rota existente (in-place) a partir do resultado de solve/plan.
 */
export async function updateMainRouteGeometry(mainRota, planResult) {
  if (!mainRota || mainRota.id_rota == null) {
    return { ok: false, message: "rota inválida" };
  }
  if (!planResult || !planResult.geojson) {
    return { ok: false, message: "sem geometria para atualizar" };
  }
  const geojsonStr =
    typeof planResult.geojson === "string"
      ? planResult.geojson
      : JSON.stringify(planResult.geojson);
  const shapeStr =
    planResult.shape == null
      ? mainRota.shape || null
      : typeof planResult.shape === "string"
        ? planResult.shape
        : JSON.stringify(planResult.shape);

  // Recarrega a linha completa e preserva identidade do banco: edição de traçado
  // não pode sobrescrever empresa/cliente com dados parciais do mapa.
  let base = { ...mainRota };
  try {
    const res = await apiController.request(apiController.endpoints.getRotaporid, {
      params: { id: mainRota.id_rota },
      silent: true,
    });
    const row = Array.isArray(res.data) ? res.data[0] : res.data;
    if (row && row.id_rota != null) {
      base = {
        ...row,
        id_rota: row.id_rota,
        cod_rota: row.cod_rota,
        nome_rota: row.nome_rota,
        id_empresa: row.id_empresa,
        id_cliente: row.id_cliente,
        ativa: row.ativa,
        observacoes: row.observacoes,
        distancia: row.distancia,
      };
    }
  } catch (e) {
    /* usa mainRota */
  }

  if (base.id_empresa == null || base.cod_rota == null || base.nome_rota == null) {
    return { ok: false, message: "dados da rota incompletos para atualizar" };
  }
  if (!shapeStr) {
    return { ok: false, message: "sem geometria para atualizar" };
  }

  try {
    await apiController.request(apiController.endpoints.updateRota, {
      data: {
        id_rota: base.id_rota,
        cod_rota: base.cod_rota,
        nome_rota: base.nome_rota,
        shape: shapeStr,
        geojson: geojsonStr,
        distancia:
          planResult.distance != null ? planResult.distance : base.distancia || 0,
        ativa: base.ativa != null ? base.ativa : 1,
        observacoes: base.observacoes != null ? base.observacoes : null,
        id_empresa: base.id_empresa,
        id_cliente: base.id_cliente != null && base.id_cliente !== "" ? base.id_cliente : null,
      },
      silent: true,
    });
    return { ok: true };
  } catch (e) {
    const data = e && e.response && e.response.data;
    const message =
      (data && (data.mensagem || data.message || data.msg || data.error)) ||
      (e && e.message) ||
      "falha ao atualizar rota";
    return { ok: false, message };
  }
}

/**
 * Calcula a reroteirização da linha pelas cercas SEM gravar nada.
 * O retorno alimenta tanto a comparação antes/depois na tela quanto
 * `aplicarRoteirizacaoPorCercas`, que persiste a prévia aprovada pelo usuário.
 *
 * @param {object} params
 * @param {number} params.idLinha
 * @param {number} params.sentido
 * @param {number} [params.idRota] rota explícita (senão usa a principal do sentido)
 * @param {number} [params.serviceSec]
 * @param {boolean} [params.fixarPontoInicial=true] Com `true` o ponto inicial (tipo 2)
 *   ancora a rota e nunca muda de posição. Com `false` ele entra como cerca comum e
 *   pode ser reordenado; quem ficar em primeiro assume o tipo de ponto inicial.
 * @returns {Promise<object>} `{ ok: false, reason }` ou a prévia completa.
 */
export async function previewRoteirizacaoPorCercas({
  idLinha,
  sentido, 
  idRota,
  serviceSec = 0,
  fixarPontoInicial = true,
} = {}) {
  if (idLinha == null) return { ok: false, reason: "noLine" };
  const sentidoNum = Number(sentido);
  if (!Number.isFinite(sentidoNum)) return { ok: false, reason: "noSentido" };

  const rotaRows = await fetchLineRoutes(idLinha);
  const rotasSentido = (rotaRows || []).filter(
    (r) => r && Number(r.id_sentido) === sentidoNum
  );
  let mainRota = null;
  if (idRota != null && idRota !== "") {
    mainRota = rotasSentido.find((r) => String(r.id_rota) === String(idRota)) || null;
    if (!mainRota) return { ok: false, reason: "noRoute" };
  } else {
    mainRota = selectMainRouteForSentido(rotaRows, sentidoNum);
    if (!mainRota) return { ok: false, reason: "noRoute" };
  }

  const cercasLinha = await fetchLineFences(idLinha);
  if (!cercasLinha.length) return { ok: false, reason: "noFences" };

  // Sentido escolhido pelo usuário: só as cercas dele entram na roteirização.
  const { sentido: sentidoCtx, cercasBase, preservedExtraCercas } = resolveDirectionContext(
    cercasLinha,
    mainRota,
    sentidoNum,
    { strict: true }
  );
  if (!cercasBase.length) return { ok: false, reason: "noFences" };

  const enriched = enrichCercasWithCoords(cercasBase);
  const cercasAntes = fenceSnapshotForHistory(enriched);
  const hasDisplacement = lineHasDisplacement(rotaRows, sentidoNum);
  // `fixarPontoInicial` vence inclusive o deslocamento: é a escolha explícita do usuário.
  const pinStartOverride = !!fixarPontoInicial;
  // O sentido escolhido pelo usuário decide de qual terminal a rota parte.
  const inverterTerminais = isSentidoVolta(sentidoCtx);
  const builtPreview = buildSolveJobsAndVehicle({
    cercas: enriched,
    hasDisplacement,
    serviceSec: 0,
    pinStart: pinStartOverride,
    inverterTerminais,
    sentido: sentidoCtx,
  });
  if (builtPreview.error) {
    return { ok: false, reason: "insufficientStops", message: builtPreview.message };
  }

  let ordered = enriched;
  let planResult = null;
  let orderChanged = false;
  let typesChanged = false;
  let tiposAlterados = [];
  let warning = null;
  const svc = Math.max(0, Number(serviceSec) || 0);

  const solved = await solveReorderLineWithPcd({
    orderedCercas: enriched,
    serviceSec: svc,
    sentido: sentidoCtx,
    hasDisplacement,
    pinStart: pinStartOverride,
    inverterTerminais,
  });
  if (solved && !solved.error && Array.isArray(solved.orderedCercas) && solved.orderedCercas.length) {
    orderChanged = fenceOrderKey(enriched) !== fenceOrderKey(solved.orderedCercas);
    tiposAlterados = solved.tiposAlterados || collectFenceTypeChanges(enriched, solved.orderedCercas);
    typesChanged = tiposAlterados.length > 0;
    ordered = solved.orderedCercas;
    planResult = solved;
  } else {
    if (solved && solved.message) warning = solved.message;
    const planned = await planOrderedFencesRoute(enriched, {
      serviceTime: svc,
      sentido: sentidoCtx,
      hasDisplacement,
      pinStart: pinStartOverride,
      inverterTerminais,
    });
    if (!planned || planned.error || !planned.geojson) {
      return {
        ok: false,
        reason: "planFailed",
        message: (planned && planned.message) || warning || null,
      };
    }
    ordered = retagOperationalTypes(enriched, sentidoCtx, {
      pinStart: builtPreview.pinStart,
      startFence: builtPreview.startFence,
      endFence: builtPreview.endFence,
      preservarTerminais: inverterTerminais,
    });
    tiposAlterados = collectFenceTypeChanges(enriched, ordered);
    typesChanged = tiposAlterados.length > 0;
    planResult = planned;
    orderChanged = false;
  }

  if (!planResult.geojson) {
    const planned = await planOrderedFencesRoute(ordered, {
      serviceTime: svc,
      sentido: sentidoCtx,
      hasDisplacement,
      pinStart: pinStartOverride,
      inverterTerminais,
    });
    if (!planned || planned.error || !planned.geojson) {
      return {
        ok: false,
        reason: "planFailed",
        message: (planned && planned.message) || warning || null,
      };
    }
    planResult = { ...planResult, ...planned };
  }

  return {
    ok: true,
    idLinha,
    sentido: sentidoCtx,
    orderChanged,
    typesChanged,
    tiposAlterados,
    pinStart: !!(solved && solved.pinStart != null ? solved.pinStart : builtPreview.pinStart),
    fixarPontoInicial: pinStartOverride,
    inverterTerminais,
    hasDisplacement,
    cercasAntes,
    cercasOriginais: enriched,
    orderedCercas: ordered,
    preservedExtraCercas,
    warning,
    mainRota,
    rotaRows,
    planResult,
  };
}

/**
 * Grava uma prévia gerada por `previewRoteirizacaoPorCercas`: regrava a sequência de
 * cercas (quando ordem/tipos mudaram) e atualiza a geometria da rota principal.
 */
export async function aplicarRoteirizacaoPorCercas(preview) {
  if (!preview || !preview.ok) {
    return { ok: false, reason: (preview && preview.reason) || "planFailed" };
  }
  const {
    idLinha,
    sentido,
    mainRota,
    rotaRows,
    orderedCercas,
    preservedExtraCercas,
    planResult,
    orderChanged,
    typesChanged,
  } = preview;

  if (orderChanged || typesChanged) {
    const persisted = await persistLineFenceSequence({
      idLinha,
      sentido,
      mainRota,
      otherRotas: (rotaRows || []).filter(
        (r) => r && mainRota && String(r.id_rota) !== String(mainRota.id_rota)
      ),
      orderedCercas,
      preservedExtraCercas,
      planGeojson: planResult.geojson,
      planDistance: planResult.distance,
      planShape: planResult.shape,
      etasByCerca: planResult.etasByCerca,
      durationSec: planResult.durationSec,
      replaceRoute: false,
    });
    if (!persisted || !persisted.ok) {
      return {
        ok: false,
        reason: "persistFailed",
        message: (persisted && persisted.message) || null,
      };
    }
  }

  const updated = await updateMainRouteGeometry(mainRota, planResult);
  if (!updated.ok) {
    return {
      ok: false,
      reason: "updateFailed",
      message: updated.message || null,
    };
  }

  return { ...preview, ok: true };
}

/**
 * Linhas da comparação antes/depois de uma prévia, para exibição na tela.
 * Distâncias em metros; `eta_segundos` é o tempo de chegada previsto pelo plano novo.
 */
export function buildRoteirizacaoComparison(preview) {
  if (!preview || !preview.ok) return null;
  const antes = preview.cercasOriginais || [];
  const depois = preview.orderedCercas || [];
  const posAntesById = new Map();
  const cercaAntesById = new Map();
  antes.forEach((c, i) => {
    if (!c || c.id_cerca == null) return;
    posAntesById.set(String(c.id_cerca), i + 1);
    cercaAntesById.set(String(c.id_cerca), c);
  });
  const etas = (preview.planResult && preview.planResult.etasByCerca) || {};

  const linhas = depois.map((c, i) => {
    const key = String(c.id_cerca);
    const anterior = cercaAntesById.get(key) || null;
    const posicaoAntes = posAntesById.has(key) ? posAntesById.get(key) : null;
    const tipoAntes = anterior != null ? Number(anterior.id_tipo_cerca) : null;
    const tipoDepois = Number(c.id_tipo_cerca);
    const eta = Number(etas[key]);
    return {
      id_cerca: c.id_cerca,
      nome_cerca: c.nome_cerca || c.cod_cerca || c.id_cerca,
      posicao_antes: posicaoAntes,
      posicao_depois: i + 1,
      tipo_antes: Number.isFinite(tipoAntes) ? tipoAntes : null,
      tipo_depois: Number.isFinite(tipoDepois) ? tipoDepois : null,
      eta_segundos: Number.isFinite(eta) ? eta : null,
      posicao_alterada: posicaoAntes != null && posicaoAntes !== i + 1,
      tipo_alterada: tipoAntes != null && tipoAntes !== tipoDepois,
    };
  });

  const idsDepois = new Set(depois.filter((c) => c && c.id_cerca != null).map((c) => String(c.id_cerca)));
  const removidas = antes
    .filter((c) => c && c.id_cerca != null && !idsDepois.has(String(c.id_cerca)))
    .map((c) => ({ id_cerca: c.id_cerca, nome_cerca: c.nome_cerca || c.id_cerca }));

  const distanciaAntes = rotaDistanciaMetros(preview.mainRota);
  const distanciaDepois = Number(preview.planResult && preview.planResult.distance);
  const duracaoDepois = Number(preview.planResult && preview.planResult.durationSec);

  return {
    linhas,
    removidas,
    total_paradas: linhas.length,
    posicoes_alteradas: linhas.filter((l) => l.posicao_alterada).length,
    tipos_alterados: linhas.filter((l) => l.tipo_alterada).length,
    distancia_antes: Number.isFinite(distanciaAntes) ? distanciaAntes : null,
    distancia_depois: Number.isFinite(distanciaDepois) ? distanciaDepois : null,
    diferenca_distancia:
      Number.isFinite(distanciaAntes) && Number.isFinite(distanciaDepois)
        ? distanciaDepois - distanciaAntes
        : null,
    duracao_depois: Number.isFinite(duracaoDepois) ? duracaoDepois : null,
    ordem_alterada: !!preview.orderChanged,
    tipos_alterados_flag: !!preview.typesChanged,
  };
}

/**
 * Otimiza a ordem das cercas intermediárias da linha (VROOM /solve) no sentido informado
 * e redesenha a rota principal in-place para seguir a sequência final.
 * Equivale a `previewRoteirizacaoPorCercas` seguido de `aplicarRoteirizacaoPorCercas`.
 *
 * @param {object} params
 * @param {number} params.idLinha
 * @param {number} params.sentido
 * @param {number} [params.serviceSec]
 * @returns {Promise<{ ok: boolean, reason?: string, message?: string, orderChanged?: boolean, warning?: string }>}
 */
export async function roteirizarLinhaPorCercas({
  idLinha,
  sentido,
  serviceSec = 0,
  fixarPontoInicial = true,
} = {}) {
  const preview = await previewRoteirizacaoPorCercas({
    idLinha,
    sentido,
    serviceSec,
    fixarPontoInicial,
  });
  if (!preview || !preview.ok) return preview;
  return aplicarRoteirizacaoPorCercas(preview);
}
